import * as actions from "./actions";

import API from "api";
import { NetworkRequest } from "api/actions";
import { MonthEndStatus } from "modules/maintenance/models";
import { ApplicationState, AppThunkAction } from "store";

export type HandledAction = actions.GetMonthEndStatusInitAction | actions.GetMonthEndStatusCompleteAction;

export type KnownAction = HandledAction | NetworkRequest;

/**
 * Used when the status could not be retrieved. This mirrors the API's own fail-closed behaviour -
 * if we cannot tell whether month end is running, assume it is.
 */
const UNKNOWN_STATUS: MonthEndStatus = {
    isInProgress: true,
    state: "UNKNOWN",
    startTime: null,
    endTime: null
};

/**
 * Coalesces concurrent requests onto a single call.
 *
 * The status is deliberately not cached anywhere - a cached value would be wrong in both directions
 * around the start and end of the job - so this only shares a request that is already in flight.
 * Callers still always get the result of a live call, never a stale one.
 */
let inFlightRequest: Promise<MonthEndStatus> | null = null;

export const actionCreators = {
    getMonthEndStatus:
        (): AppThunkAction<KnownAction> =>
        (dispatch, getState: () => ApplicationState) => {
            if (inFlightRequest) {
                return inFlightRequest;
            }

            dispatch({ type: "GET_MONTH_END_STATUS_INIT" });

            inFlightRequest = dispatch(API.maintenance.getMonthEndStatus())
                .then((response) => {
                    if (response?.status === 200) {
                        return response.json() as Promise<MonthEndStatus>;
                    }
                    return null;
                })
                .then((monthEnd: MonthEndStatus) => {
                    if (monthEnd) {
                        dispatch({ type: "GET_MONTH_END_STATUS_COMPLETE", monthEnd, succeeded: true });
                        return monthEnd;
                    }

                    dispatch({ type: "GET_MONTH_END_STATUS_COMPLETE", monthEnd: UNKNOWN_STATUS, succeeded: false });
                    return UNKNOWN_STATUS;
                })
                .catch(() => {
                    dispatch({ type: "GET_MONTH_END_STATUS_COMPLETE", monthEnd: UNKNOWN_STATUS, succeeded: false });
                    return UNKNOWN_STATUS;
                })
                .finally(() => {
                    inFlightRequest = null;
                });

            return inFlightRequest;
        }
};
